import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  Box, Link, Typography,
} from '@worthy-npm/worthy-common-ui-components';
import { IMask } from 'react-imask';
import { Step, StepGridContainer } from '../../../styles/common';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  IRegistration,
  postRegEstimation,
  registerUser,
  selectFlow,
  selectRegister,
  selectRegisterAPI,
  selectSubmitAPI,
  selectValueCategory,
  updateRegistration,
  updateRegistrationOnLogin,
  updateUserPhoneNumber,
} from '../../../slices/submitSlice';
import { StepProps } from '../common';
import { GoogleSignIn } from '../../google_signin';
import { CookieStore } from '../../../services/cookieStore';
import { LoadingContainer, LoadingOverlay, LoadingDiamondImg } from '../../overlay';
import StepTitle from '../../stepTitle';
import { StepRegistrationForm } from './step_registration_form';
import { StepRegistrationLoginForm } from './step_registration_login';
import { setItemIdToWindow } from '../../../lib/item';
import GA from '../../../data/GA';
import { PromoDetails } from '../../../data/promotion';
import { DEFAULT_PHONE } from '../../../services/worthyAPI';
import { FooterIcon, LogInContainer, StepContainerTopAligned } from './step_registration.styles';

export function GoogleSignInEnabled() {
  const googleClientID = process.env.REACT_APP_GOOGLE_SIGNIN_CLIENT_ID || '';
  return googleClientID !== '' && CookieStore.getGoogleSigninExp();
}

export const termsConditionsLink = (
  <Link
    color="highlight.main"
    href={`${process.env.REACT_APP_SITE_URL}/about/terms-and-conditions`}
    target="_blank"
  >
    terms & conditions
  </Link>
);

export const privacyPolicyLink = (
  <Link
    color="highlight.main"
    href={`${process.env.REACT_APP_SITE_URL}/about/privacy-policy`}
    target="_blank"
  >
    privacy policy
  </Link>
);

const normalizePhoneNumber = (value: string, mask = true) => {
  if (!value) {
    return '';
  }
  // Handle DEFAULT_PHONE specially - return as-is
  if (value === DEFAULT_PHONE) {
    return value;
  }
  const masked = IMask.createMask({
    mask: '+1 (000) 000-0000',
  });
  masked.resolve(value);
  return mask ? masked.value : masked.unmaskedValue;
};

function StepRegistration({
  active, next, stepName, idx, prev, props,
}: StepProps) {
  const registerAPI = useAppSelector(selectRegisterAPI);
  const registration = useAppSelector(selectRegister);
  const dispatch = useAppDispatch();
  const valueCategory = useAppSelector(selectValueCategory);

  const [verificationView, setVerificationView] = useState(false);
  const [loginView, setLoginView] = useState(false);
  const [loginType, setLoginType] = useState<'email' | 'phone'>('email');
  const [isLoginFromError, setIsLoginFromError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { itemId } = useAppSelector(selectSubmitAPI);
  const showPromoTerms = PromoDetails.enabled();

  // Only show loader to prevent disabling from Google signin button
  // and do it manually after esimation event
  const showLoader = (_state: boolean) => {
    setLoading(true);
  };
  const enableVerification = () => CookieStore.getVerifyPhoneNumberExp()
    && CookieStore.getGoogleSigninExp();
  const getStepName = () => (verificationView ? 'StepVerification' : stepName);

  useEffect(() => {
    setLoading(registerAPI.loading);
  }, [registerAPI, valueCategory]);

  useEffect(() => {
    if (!active) return;

    GA.registrationStepView(stepName);
  }, [active]);

  setItemIdToWindow(itemId);

  interface IUser {
    id: number;
    phone: string;
    email: string;
  }

  const onSuccessfulGoogleLogin = async (
    user: IUser | undefined,
    isUserLogin: boolean,
    leadId: number,
  ) => {
    // send an event on successful Google
    GA.successfullGoogleRegister(isUserLogin);

    await dispatch(postRegEstimation({ userId: +(user?.id || 0) }));

    setLoading(false);

    // Enable verification if experiment is enabled
    // and user is new and user doesn't have phone number
    if (enableVerification() && !isUserLogin && (!user?.phone || user?.phone === '0000000001')) {
      setVerificationView(true);
    } else {
      next('' as any);
    }
  };

  const onPhoneVerificationSubmit = (data: IRegistration) => {
    if (registerAPI.loading) {
      return;
    }

    const registerWithPhone = { ...registration, phoneNumber: data.phoneNumber };
    dispatch(
      updateUserPhoneNumber({
        data: registerWithPhone,
        callBack: () => { },
        successCallBack: () => {
          next('' as any);
        },
      } as any) as any,
    );
  };

  const onRegistrationSubmit = (data: IRegistration) => {
    if (registerAPI.loading) {
      return;
    }

    dispatch(updateRegistration(data));
    dispatch(
      registerUser({
        data,
        clb: () => { },
        successClb: async (response: any) => {
          await dispatch(postRegEstimation({ userId: response?.user?.id }));
          const consent = data.smsConsent ? data.smsConsent.toString() : 'false';

          GA.registrationSuccess(
            'valueCategory',
            valueCategory ? _.camelCase(valueCategory) : undefined,
            'submissionFlowType',
            'submitByForm',
            'smsConsent',
            consent,
          );
          next('' as any);
        },
      } as any) as any,
    );
  };

  const onSubmit = (data: IRegistration, smsConsent: boolean) => {
    setLoading(true);

    const preparedData = {
      ...data,
      ...{ phoneNumber: normalizePhoneNumber(data.phoneNumber, false) },
    };

    if (verificationView) {
      onPhoneVerificationSubmit(preparedData);
    } else {
      onRegistrationSubmit({ ...preparedData, smsConsent });
    }
  };

  const onLogin = async (data: IRegistration) => {
    setLoading(true);

    dispatch(updateRegistrationOnLogin(data));
    // GA.loginSuccess(
    //   'valueCategory',
    //   _.camelCase(valueCategory),
    //   'submissionFlowType',
    //   byImg ? 'submitByImage' : 'submitByForm',
    //   'smsConsent',
    //   consent,
    // );
    // await dispatch(postRegEstimation({ userId: +(data.user?.id || 0) }));
    next('' as any);
  };

  const handleLoginView = (byMedium = false) => {
    if (byMedium && registerAPI.errorObj.validationField) {
      setLoginType(registerAPI.errorObj.validationField);
      setIsLoginFromError(true);
    } else {
      setLoginType('email');
      setIsLoginFromError(false);
    }
    setLoginView(true);
  };

  return (
    <LoadingContainer>
      {loading && (
        <LoadingOverlay>
          <LoadingDiamondImg />
        </LoadingOverlay>
      )}

      <StepContainerTopAligned
        data-automation={verificationView ? 'verification-step' : 'registration-step'}
      >
        <Step>
          <StepTitle
            prev={prev}
            stepTitle={loginView ? `Log in with ${loginType}` : 'See how much you can get!'}
            stepNotice=""
            stepCaption={loginView ? 'Log in' : 'Sign Up'}
            optionalProps={props || {}}
          />

          <StepGridContainer style={{ maxWidth: '406px' }}>
            {!loginView && (
              <StepRegistrationForm
                onSubmit={onSubmit}
                handleLoginView={handleLoginView}
                verificationView={verificationView}
                stepName={getStepName()}
                idx={idx}
                showPromoTerms={false}
              />
            )}

            {!loginView && !verificationView && (
              <>
                <Box marginTop={3}>
                  <LogInContainer>
                    <Typography variant="body1" fontWeight="bold">
                      Already signed up?&nbsp;
                      <Link
                        color="highlight.main"
                        onClick={() => handleLoginView(false)}
                        sx={{ cursor: 'pointer' }}
                      >
                        Log in
                      </Link>
                    </Typography>
                  </LogInContainer>
                </Box>
                <Typography variant="body1" marginTop="24px" textAlign="center" fontSize="12px">
                  By signing up, you agree to the Worthy
                  {' '}
                  {termsConditionsLink}
                  {' '}
                  and to receive
                  marketing marketing communications from Worthy in accordance with our
                  {' '}
                  {privacyPolicyLink}
                  {' '}
                  (including CCPA requirements).
                </Typography>
              </>
            )}

            {loginView && (
              <>
                <StepRegistrationLoginForm
                  onLogin={onLogin}
                  initialValue={loginType === 'email' ? registration.email : registration.phoneNumber}
                  valueType={loginType}
                  verificationView={verificationView}
                  stepName={getStepName()}
                  idx={idx}
                  showPromoTerms={false}
                  readOnlyValue={isLoginFromError}
                />
                <Box marginTop={3}>
                  <LogInContainer>
                    <Typography variant="body1" fontWeight="bold">
                      Don&apos;t have an account?&nbsp;
                      <Link
                        color="highlight.main"
                        onClick={() => setLoginView(false)}
                        sx={{ cursor: 'pointer' }}
                      >
                        Register
                      </Link>
                    </Typography>
                  </LogInContainer>
                </Box>
              </>
            )}

            {GoogleSignInEnabled() && (
              <Box height="76px" marginTop={3} display="flex" justifyContent="center">
                <GoogleSignIn
                  onSuccess={onSuccessfulGoogleLogin}
                  setLoading={showLoader}
                  disabled={verificationView}
                  options={{ width: '350px', size: 'large', shape: 'circle' }}
                />
              </Box>
            )}

            {!loginView && (
              <Box display="flex" justifyContent="center" marginTop="24px" marginBottom="25px">
                <FooterIcon src="/images/icons/bbb.svg" />
                <FooterIcon src="/images/icons/secured.svg" />
              </Box>
            )}
            {showPromoTerms && (
              <Typography variant="body1" textAlign="justify" fontSize="small" color="secondary">
                {PromoDetails.terms}
              </Typography>
            )}
          </StepGridContainer>
        </Step>
      </StepContainerTopAligned>
    </LoadingContainer>
  );
}

export default StepRegistration;
